import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import type { LookupState } from '../types';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import {
  notificationSettingDefinitions,
  validateNotificationThreshold,
  type EffectiveNotificationSetting,
  type NotificationSettingDefinition,
  type NotificationSettingRow,
} from '../utils/notificationSettings';

type NotificationSettingsProps = {
  lookups: LookupState;
  readOnly?: boolean;
};

type DraftMap = Record<string, EffectiveNotificationSetting>;
type Snack = { severity: 'success' | 'error'; message: string };

const severityOptions = [
  { value: 'Informational', label: 'Info' },
  { value: 'Warning', label: 'Warning' },
  { value: 'Critical', label: 'Critical' },
] as const;

function defaultDraft(definition: NotificationSettingDefinition): EffectiveNotificationSetting {
  return {
    notificationKey: definition.notificationKey,
    enabled: definition.defaultEnabled,
    severity: definition.defaultSeverity,
    thresholdValue: definition.defaultThresholdValue,
    thresholdUnit: definition.defaultThresholdUnit,
  };
}

function defaultsByKey(): DraftMap {
  return notificationSettingDefinitions.reduce<DraftMap>((acc, definition) => {
    acc[definition.notificationKey] = defaultDraft(definition);
    return acc;
  }, {});
}

function settingFromRow(definition: NotificationSettingDefinition, row?: NotificationSettingRow): EffectiveNotificationSetting {
  if (!row) return defaultDraft(definition);
  return {
    notificationKey: definition.notificationKey,
    enabled: row.enabled,
    severity: row.severity || definition.defaultSeverity,
    thresholdValue: definition.defaultThresholdUnit ? row.threshold_value ?? definition.defaultThresholdValue : null,
    thresholdUnit: definition.defaultThresholdUnit,
  };
}

function thresholdLabel(definition: NotificationSettingDefinition): string {
  if (!definition.defaultThresholdUnit) return '';
  if (definition.notificationKey === 'players_below_target') return 'Matches below target';
  if (definition.notificationKey === 'player_not_played_recently') return 'Weeks since last played';
  if (definition.notificationKey === 'tennisrecord_edr_sync_due') return 'Weeks since EDR sync';
  if (definition.notificationKey === 'match_scores_required') return 'Hours after match';
  return `${definition.defaultThresholdUnit} before`;
}

export function NotificationSettings({ lookups, readOnly = false }: NotificationSettingsProps) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'notification-settings',
    defaultState: { selectedWorkspaceId: '' },
  });
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(savedViewState.selectedWorkspaceId);
  const [drafts, setDrafts] = useState<DraftMap>(() => defaultsByKey());
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'notification-settings', ready: !loading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedWorkspaceId(savedViewState.selectedWorkspaceId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedWorkspaceId });
  }, [selectedWorkspaceId, setSavedViewState, viewStateKey, viewStateReady]);

  useEffect(() => {
    if (!selectedWorkspaceId && lookups.workspaces.length > 0) setSelectedWorkspaceId(lookups.workspaces[0].value);
  }, [lookups.workspaces, selectedWorkspaceId]);

  const selectedWorkspaceName = lookups.workspaces.find((workspace) => workspace.value === selectedWorkspaceId)?.label || 'Workspace';

  const groupedDefinitions = useMemo(() => ({
    actionRequired: notificationSettingDefinitions.filter((definition) => definition.category === 'Action Required'),
    alerts: notificationSettingDefinitions.filter((definition) => definition.category === 'Alerts & Reminders'),
  }), []);

  const loadSettings = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setDrafts(defaultsByKey());
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('notification_settings')
      .select('id, workspace_id, notification_key, enabled, severity, threshold_value, threshold_unit')
      .eq('workspace_id', selectedWorkspaceId);
    setLoading(false);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    const rowsByKey = new Map(((data || []) as unknown as NotificationSettingRow[]).map((row) => [row.notification_key, row]));
    setDrafts(notificationSettingDefinitions.reduce<DraftMap>((acc, definition) => {
      acc[definition.notificationKey] = settingFromRow(definition, rowsByKey.get(definition.notificationKey));
      return acc;
    }, {}));
  }, [selectedWorkspaceId]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const updateDraft = (notificationKey: string, patch: Partial<EffectiveNotificationSetting>) => {
    setDrafts((current) => ({
      ...current,
      [notificationKey]: {
        ...current[notificationKey],
        ...patch,
      },
    }));
  };

  const saveSetting = async (definition: NotificationSettingDefinition) => {
    if (readOnly || !selectedWorkspaceId) return;
    const draft = drafts[definition.notificationKey] || defaultDraft(definition);
    const validationError = validateNotificationThreshold(definition, draft.thresholdValue);
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return;
    }
    setSavingKey(definition.notificationKey);
    const { error } = await supabase.from('notification_settings').upsert({
      workspace_id: selectedWorkspaceId,
      notification_key: definition.notificationKey,
      enabled: draft.enabled,
      severity: draft.severity,
      threshold_value: definition.defaultThresholdUnit ? draft.thresholdValue : null,
      threshold_unit: definition.defaultThresholdUnit,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'workspace_id,notification_key' });
    setSavingKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Notification setting saved.' });
  };

  const resetSetting = async (definition: NotificationSettingDefinition) => {
    if (readOnly || !selectedWorkspaceId) return;
    setSavingKey(definition.notificationKey);
    const { error } = await supabase
      .from('notification_settings')
      .delete()
      .eq('workspace_id', selectedWorkspaceId)
      .eq('notification_key', definition.notificationKey);
    setSavingKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    updateDraft(definition.notificationKey, defaultDraft(definition));
    setSnack({ severity: 'success', message: 'Notification setting reset to default.' });
  };

  const resetAll = async () => {
    if (readOnly || !selectedWorkspaceId) return;
    if (!window.confirm(`Reset all notification settings for ${selectedWorkspaceName} to defaults?`)) return;
    setLoading(true);
    const { error } = await supabase.from('notification_settings').delete().eq('workspace_id', selectedWorkspaceId);
    setLoading(false);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setDrafts(defaultsByKey());
    setSnack({ severity: 'success', message: 'All notification settings reset to defaults.' });
  };

  const renderCard = (definition: NotificationSettingDefinition) => {
    const draft = drafts[definition.notificationKey] || defaultDraft(definition);
    const validationError = validateNotificationThreshold(definition, draft.thresholdValue);
    return (
      <Paper key={definition.notificationKey} variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{definition.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">{definition.description}</Typography>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={draft.enabled} onChange={(event) => updateDraft(definition.notificationKey, { enabled: event.target.checked })} disabled={readOnly} />}
              label="Enabled"
              sx={{ mr: 0 }}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: definition.defaultThresholdUnit ? '1fr 1fr 1fr' : '1fr' }, gap: 1.5 }}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={draft.severity}
                onChange={(event) => updateDraft(definition.notificationKey, { severity: event.target.value as EffectiveNotificationSetting['severity'] })}
                disabled={readOnly}
              >
                {severityOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            {definition.defaultThresholdUnit && (
              <>
                <TextField
                  label={thresholdLabel(definition)}
                  type="number"
                  value={draft.thresholdValue ?? ''}
                  onChange={(event) => updateDraft(definition.notificationKey, { thresholdValue: event.target.value === '' ? null : Number(event.target.value) })}
                  error={Boolean(validationError)}
                  helperText={validationError || `${definition.minThresholdValue}-${definition.maxThresholdValue} ${definition.defaultThresholdUnit.toLowerCase()}`}
                  disabled={readOnly}
                  fullWidth
                />
                <TextField label="Unit" value={definition.defaultThresholdUnit} disabled fullWidth />
              </>
            )}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<RestoreIcon />} onClick={() => void resetSetting(definition)} disabled={readOnly || savingKey === definition.notificationKey}>
              Reset to Default
            </Button>
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={() => void saveSetting(definition)} disabled={readOnly || Boolean(validationError) || savingKey === definition.notificationKey}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Notification Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure Command Center notification thresholds, severities, and enabled states by workspace.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>General</Typography>
          {loading && <LinearProgress />}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl sx={{ minWidth: 280 }}>
              <InputLabel>Workspace</InputLabel>
              <Select label="Workspace" value={selectedWorkspaceId} onChange={(event) => setSelectedWorkspaceId(event.target.value)}>
                {lookups.workspaces.map((workspace) => <MenuItem key={workspace.value} value={workspace.value}>{workspace.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<RestoreIcon />} onClick={() => void resetAll()} disabled={readOnly || !selectedWorkspaceId}>
              Reset All Notification Settings
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Action Required</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {groupedDefinitions.actionRequired.map(renderCard)}
        </Box>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Alerts & Reminders</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {groupedDefinitions.alerts.map(renderCard)}
        </Box>
      </Box>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
